import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import { cn } from '@/utils/utils';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';
import { kickboardClassName } from '@/configs/constants';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        className={cn(
            'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        {...props}
        ref={ref}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const baseAnimate: string = 'transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500';
const sheetVariants = cva(
    'fixed z-50 gap-4 bg-background p-6 shadow-lg ',
    {
        variants: {
            side: {
                top: `${baseAnimate} inset-x-0 top-0 border-none data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top`,
                bottom: `${baseAnimate} inset-x-0 bottom-0 border-none data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
                left: `${baseAnimate} inset-y-0 left-0 h-full w-3/4 border-none data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm`,
                right: `${baseAnimate} inset-y-0 right-0 h-full w-3/4  border-none data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm`,
                menuEu: 'inset-y-0 right-0 h-full w-3/4  border-none data-[state=open]:animate-menu-in data-[state=closed]:animate-menu-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right  sm:max-w-sm'
            }
        },
        defaultVariants: {
            side: 'right'
        }
    }
);

interface SheetContentProps
    extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
    sheetOverlayClassName?: string
}

const SheetContent = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Content>,
    SheetContentProps
>(({ side = 'right', className, children, sheetOverlayClassName, ...props }, ref) => (
    <SheetPortal>
        <SheetOverlay className={sheetOverlayClassName} />
        <SheetPrimitive.Content
            ref={ref}
            className={cn(sheetVariants({ side }), className)}
            {...props}
        >
            {children}
            <SheetPrimitive.Close className='absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary'>
                <XIcon className='h-4 w-4 text-[#fff]' />
                {/* <span className='sr-only'>Close</span> */}
            </SheetPrimitive.Close>
        </SheetPrimitive.Content>
    </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

interface SheetContentCusProps extends SheetContentProps {
    closeNode: React.ReactNode
}
const SheetContentCus = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Content>,
    SheetContentCusProps
>(({ side = 'right', className, children, closeNode, sheetOverlayClassName, ...props }, ref) => {
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    return (
        <SheetPortal>
            <SheetOverlay className={sheetOverlayClassName} />
            <SheetPrimitive.Content
                ref={ref}
                className={cn(
                    sheetVariants({ side }),
                    className,
                    'z-[61]',
                    isEk ? `${kickboardClassName}` : ''
                )}
                {...props}
            >
                {children}
                <SheetPrimitive.Close className='absolute right-0 top-0 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary'>
                    {closeNode}
                </SheetPrimitive.Close>
            </SheetPrimitive.Content>
        </SheetPortal>
    );
});
SheetContentCus.displayName = 'SheetContentCus';

const SheetHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-col space-y-2 text-center sm:text-left',
            className
        )}
        {...props}
    />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
            className
        )}
        {...props}
    />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold text-foreground', className)}
        {...props}
    />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        {...props}
    />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
    Sheet,
    SheetPortal,
    SheetOverlay,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
    SheetContentCus
};
